import * as React from 'react';
import styled from 'styled-components';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ErrorText } from '../../../shared/form/ErrorMessage';
import { TextField } from '../../../shared/form/TextField';

export interface IIdentifyProps {
  onEmailChange: (value: string) => void;
  email: string;
  error?: string;
}

export const Identify = React.memo(
  (props: IIdentifyProps): React.ReactElement => {
    const [localError, setLocalError] = React.useState<string>('');

    /**
     * Validate email format using regex
     */
    const validateEmail = (email: string): boolean => {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(email);
    };

    /**
     * Handle email blur event to validate format
     */
    const handleEmailBlur = (): void => {
      if (props.email.length === 0) {
        setLocalError('Please enter your email address');
      } else if (!validateEmail(props.email)) {
        setLocalError('Please enter a valid email address');
      } else {
        setLocalError('');
      }
    };

    /**
     * Handle email change to clear validation errors
     */
    const handleEmailChange = (value: string): void => {
      setLocalError('');
      props.onEmailChange(value);
    };

    const displayError = props.error || localError;
    const errorId = 'email-error';

    return (
      <React.Fragment>
        <h1 id='login-heading' style={{ margin: '.5rem 1rem' }}>
          Enter Email
        </h1>
        <InputContainer role='form' aria-labelledby='login-heading'>
          <ControlSpacer>
            <TextField
              type='email'
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              label='Email'
              style={{ width: '100%' }}
              name='username'
              value={props.email}
              role='textbox'
              aria-required='true'
              aria-invalid={Boolean(displayError)}
              aria-describedby={displayError ? errorId : undefined}
              autocomplete='email'
            />
          </ControlSpacer>
          {displayError && (
            <ErrorText id={errorId} role='alert' aria-live='polite'>
              {displayError}
            </ErrorText>
          )}
          <PrivacyPolicyLink
            href='https://www.hawkindynamics.com/privacy-policy'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Read our Privacy Policy (opens in new tab)'
          >
            Privacy Policy
          </PrivacyPolicyLink>
        </InputContainer>
      </React.Fragment>
    );
  },
  (
    prevProps: Readonly<IIdentifyProps>,
    nextProps: Readonly<IIdentifyProps>
  ): boolean => {
    return (
      prevProps.email === nextProps.email &&
      prevProps.error === nextProps.error &&
      prevProps.onEmailChange === nextProps.onEmailChange
    );
  }
);

Identify.displayName = 'Identify';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 20px;
  padding-top: 0px;
  padding-bottom: 10px;
  width: 100%;
  height: 100%;
`;

const PrivacyPolicyLink = styled.a`
  margin-top: 8px;
  padding: 6px 12px;
  color: #42a5f5;
  text-decoration: none;
  font-size: 12px;
  border: 1px solid #42a5f5;
  border-radius: 4px;
  text-align: center;
  align-self: flex-start;
  transition: all 200ms ease-in-out;

  &:hover {
    background-color: rgba(66, 165, 245, 0.1);
    text-decoration: none;
  }

  &:focus {
    outline: 2px solid #42a5f5;
    outline-offset: 2px;
  }
`;
